/**
 * Context-view bootstrapper.
 *
 * Loads a normal /:user/:context graph (optionally in compare mode), processes
 * its statements, seeds initial topics, and syncs URL stopWords into the
 * filter store.
 *
 * Lifted verbatim from useBootstrapStore Step 4. Behavior is intentionally
 * unchanged — same log lines, same try/catch boundaries, same skip-if-not-found
 * branch.
 */

import { useZustand } from '../../hooks/useZustand'
import { useGraphStore } from '../useGraphStore'
import { useFilterStore } from '../useFilterStore'
import {
    useContextsStore,
    findContextByName,
} from '../useContextsStore'
import {
    ACCESS_MODES,
    ACCESS_DENIED_REASONS,
} from '../contextAccess'
import { loadGraphData } from './loadGraphData'
import { processStatements, initTopicsFromGraph } from './processStatements'
import {
    getCompareModeFromUrl,
    getGraphPanelFromUrl,
    getControlsFromUrl,
} from './urlHelpers'

// Mirror of ContextDrawerItem.getContextType: label prefixes imply a special
// type (and editor route) and win over the DB type; otherwise fall back to the
// contextType stored on the contexts tuple (index 3).
export const resolveContextType = (label, dbContextType) => {
    if (label) {
        if (label.includes('meta_')) return 'META'
        if (label.includes('seo_')) return 'SEO'
        if (label.includes('niche_')) return 'NICHE'
        // KWRDS isn't stored as a DB type — it's implied by a kwrds_ prefix,
        // mirroring the default branch of getContextRoute in routes.js.
        if (label.split('_')[0] === 'kwrds') return 'KWRDS'
    }
    return dbContextType ?? null
}

export const bootstrapContextView = async ({
    contextName,
    addContexts,
    stopWords,
    graphOwnerUsername,
    isPublic,
    onLoadingStatements,
}) => {
    const contextsState = useContextsStore.getState()
    const contextExists =
        contextName && !!findContextByName(contextsState, contextName)

    // The bootstrap fetched only the owner's public list, so a URL context
    // missing from it for a cross-user viewer is either private-and-denied
    // or non-existent. We can't tell the two apart from the client; both
    // collapse to NOT_FOUND, which is honest in either case.
    const urlContextNotVisibleToCrossUserViewer =
        contextName &&
        !contextExists &&
        contextsState.currentGraphOwner?.accessMode ===
            ACCESS_MODES.PUBLIC_OF_OTHER &&
        !contextsState.accessDenied

    if (urlContextNotVisibleToCrossUserViewer) {
        useContextsStore.getState().markAccessDenied({
            reason: ACCESS_DENIED_REASONS.NOT_FOUND,
        })
        return
    }

    // The owner opening one of their own URLs for a context that doesn't
    // exist yet — a brand new graph. We can't load it (there's nothing on
    // the server), but we still want the statements panel to mount so the
    // user can add the first statement by hand. Adding it creates the
    // context server-side, recalculates the graph in place and promotes
    // the seeded context to a real one (see promoteSeededContext).
    const canSeedNewContext =
        !!contextName && !!graphOwnerUsername && !isPublic

    if (
        !contextName ||
        !graphOwnerUsername ||
        (!contextExists && !canSeedNewContext)
    ) {
        console.log(
            '[InfraNodus] Step 4: Skipping graph load (no context in URL)'
        )
        return
    }

    const compareMode = getCompareModeFromUrl(addContexts)

    // View state derived from the URL alone — applies whether or not the
    // context exists yet, so it is set before the seeded-graph branch.

    // Re-arm the auto-message gate (`home/index.jsx`): a fresh graph
    // load means the ChatWidget will re-hydrate, so its `onReady`
    // must fire again before `setForceRequestMode` can land.
    useZustand.getState().setIsAiChatReady(false)

    // Set graph layer to topics by default for all graphs. Honor an
    // explicit `?graph_panel=` (or legacy `?panel=`) from the share/embed
    // URL so views open on the layer the link was generated from.
    useZustand
        .getState()
        .setCurrentGraphMode(getGraphPanelFromUrl() || 'topics')

    // Seed the graph controls / analytics panel start state from
    // `?controls=`. Null means "let the graph use its own heuristic".
    useZustand.getState().setCurrentGraphControlsMode(getControlsFromUrl())

    // Store the compare mode the graph is being loaded with (null when not
    // a comparison) so the graph can adapt its rendering to it.
    useZustand.getState().setCurrentCompareMode(compareMode)
    // Seed additionalContexts alongside compareMode so that a recalc which
    // fires before the useAdditionalContexts hook mounts never races to the
    // single-context path (the hook also calls setAdditionalContexts, so
    // this is a safe early-seed, not a duplicate set).
    useZustand.getState().setAdditionalContexts(addContexts)

    // No length guard: a cleared URL must overwrite a stale non-empty
    // value after SPA navigation, so an empty array is a valid sync.
    const currentStopWords = useFilterStore.getState().stopWords || []
    const hasSameStopWords =
        currentStopWords.length === stopWords.length &&
        stopWords.every(word => currentStopWords.includes(word))

    if (!hasSameStopWords) {
        useFilterStore.setState({ stopWords })
    }

    if (!contextExists) {
        console.log(
            '[InfraNodus] Step 4: New context — seeding empty editable graph'
        )
        useZustand
            .getState()
            .setCurrentContextType(resolveContextType(contextName, null))
        useGraphStore.getState().seedEmptyGraph()
        // Sets currentContext + loadedStatements:true with an empty list, so
        // StatementFilterPanel (gated on loadedStatements) mounts.
        await processStatements({
            statements: [],
            contextName,
            addContexts,
        })
        useZustand.getState().setSeededEmptyContext(true)
        return
    }

    console.log('[InfraNodus] Step 4: Loading graph data', {
        graphOwnerUsername,
        contextName,
        addContexts,
        compareMode: compareMode || 'none',
    })

    try {
        useGraphStore.getState().setLoading(true)

        // Resolve and store the type of the context being opened, so the graph
        // (via LazyGraph) and other consumers can read it without re-resolving.
        const contextEntry = findContextByName(
            useContextsStore.getState(),
            contextName
        )
        useZustand
            .getState()
            .setCurrentContextType(
                resolveContextType(contextName, contextEntry?.type)
            )

        await loadGraphData({
            contextName,
            addContexts,
            compareMode,
            stopWords,
            isPublic,
            graphOwnerUsername,
        })

        // ═══════════════════════════════════════════════════
        // Step 5: Process statements and topics
        // ═══════════════════════════════════════════════════
        console.log('[Bootstrap] Step 5: Processing statements...')
        if (onLoadingStatements) onLoadingStatements()

        const statements = useGraphStore.getState().getStatements()
        // Always process — even with zero statements — so `loadedStatements`
        // flips true and the statement panel mounts on an empty graph,
        // letting the user add the first statement by hand (matches the
        // delete-all state, where the panel already renders empty).
        // (master's extra `contexts, publicContexts` args dropped:
        // processStatements now reads context records from useContextsStore.)
        await processStatements({
            statements: statements && statements.length > 0 ? statements : [],
            contextName,
            addContexts,
        })

        initTopicsFromGraph()
    } catch (err) {
        console.error('[Bootstrap] Failed to load graph:', err)
        // A locked graph is a paywall state, not a load failure: the
        // page shows the lock screen instead of the generic error toast.
        useGraphStore
            .getState()
            .setError(
                err.quotaCode === 'QUOTA_GRAPH_LOCKED'
                    ? 'QUOTA_GRAPH_LOCKED'
                    : err.message
            )
    }
}
